import { useState } from 'react';
import { XMarkIcon, TrashIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

interface FormattedPartner {
  id: string;
  company: string;
  logo: string;
  contactPerson: string;
  email: string;
  phone: string;
  website?: string;
  tier: string;
  status: string;
  contribution: string;
  contributionAmount?: number;
  startDate: string;
  endDate: string;
  benefits: string[];
  description?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  partner: FormattedPartner | null;
  tournamentId: string;
}

function tierColor(tier: string) {
  if (tier.includes('Title')) return 'bg-amber-100 text-amber-800';
  if (tier.includes('Diamond')) return 'bg-blue-100 text-blue-800';
  if (tier.includes('Platinum')) return 'bg-slate-100 text-slate-700';
  if (tier.includes('Gold')) return 'bg-yellow-100 text-yellow-700';
  if (tier.includes('Silver')) return 'bg-gray-100 text-gray-600';
  return 'bg-green-100 text-green-700';
}

function statusColor(status: string) {
  switch (status) {
    case 'active': return 'bg-green-100 text-green-700';
    case 'pending': return 'bg-amber-100 text-amber-700';
    case 'expired': return 'bg-red-100 text-red-700';
    default: return 'bg-gray-100 text-gray-600';
  }
}

const STATUS_OPTIONS = ['pending', 'active', 'expired'];

export default function MobilePartnerDetailsSheet({ isOpen, onClose, partner, tournamentId }: Props) {
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (!isOpen || !partner) return null;

  const handleStatusChange = async (newStatus: string) => {
    try {
      setUpdatingStatus(true);
      const { error } = await supabase
        .from('partners')
        .update({ status: newStatus })
        .eq('id', partner.id);
      if (error) throw error;
      toast.success(`Status updated to ${newStatus}`);
      onClose();
    } catch {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      const { error } = await supabase.from('partners').delete().eq('id', partner.id);
      if (error) throw error;
      toast.success('Partner deleted');
      onClose();
    } catch {
      toast.error('Failed to delete partner');
    } finally {
      setDeleting(false);
    }
  };

  const row = (label: string, value: string | undefined) =>
    value ? (
      <div className="flex items-start justify-between gap-4 py-2.5 border-b border-gray-50 last:border-0">
        <p className="text-xs text-gray-400 flex-shrink-0">{label}</p>
        <p className="text-xs font-medium text-golf-gray text-right">{value}</p>
      </div>
    ) : null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-t-3xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-base font-bold text-golf-gray">Partner Details</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 active:bg-gray-200 transition-colors">
            <XMarkIcon className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          <div className="flex items-center gap-4">
            {partner.logo ? (
              <img src={partner.logo} alt={partner.company} className="w-14 h-14 object-contain rounded-xl border border-gray-100 bg-gray-50 flex-shrink-0" />
            ) : (
              <div className="w-14 h-14 rounded-xl bg-teal-50 flex items-center justify-center flex-shrink-0">
                <BuildingOfficeIcon className="w-7 h-7 text-teal-600" />
              </div>
            )}
            <div>
              <p className="font-bold text-golf-gray">{partner.company}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${tierColor(partner.tier)}`}>{partner.tier}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${statusColor(partner.status)}`}>{partner.status}</span>
              </div>
              <p className="text-sm font-bold text-golf-orange mt-1">{partner.contribution}</p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl px-4">
            {row('Contact', partner.contactPerson)}
            {row('Email', partner.email)}
            {row('Phone', partner.phone)}
            {row('Website', partner.website)}
            {row('Start Date', partner.startDate)}
            {row('End Date', partner.endDate)}
          </div>

          {partner.benefits && partner.benefits.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Benefits</p>
              <div className="flex flex-wrap gap-1.5">
                {partner.benefits.map(b => (
                  <span key={b} className="text-xs bg-golf-sand/60 text-golf-gray rounded-lg px-2 py-1">{b}</span>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Update Status</p>
            <div className="flex gap-2">
              {STATUS_OPTIONS.map(s => (
                <button
                  key={s}
                  onClick={() => handleStatusChange(s)}
                  disabled={updatingStatus || partner.status === s}
                  className={`flex-1 py-2.5 rounded-xl text-xs font-semibold capitalize transition-colors disabled:opacity-40 ${
                    partner.status === s ? statusColor(s) : 'bg-gray-100 text-gray-600 active:bg-gray-200'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {!confirmDelete ? (
            <button
              onClick={() => setConfirmDelete(true)}
              className="w-full py-3 rounded-xl border border-red-200 text-sm font-semibold text-red-500 bg-red-50 active:bg-red-100 flex items-center justify-center gap-2"
            >
              <TrashIcon className="w-4 h-4" />
              Delete Partner
            </button>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3">
              <p className="text-sm font-semibold text-red-600">Delete "{partner.company}"?</p>
              <p className="text-xs text-red-500">This cannot be undone.</p>
              <div className="flex gap-2">
                <button onClick={() => setConfirmDelete(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 bg-white">Cancel</button>
                <button onClick={handleDelete} disabled={deleting} className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-semibold disabled:opacity-60">
                  {deleting ? 'Deleting...' : 'Confirm Delete'}
                </button>
              </div>
            </div>
          )}

          <div className="pb-2" />
        </div>
      </div>
    </div>
  );
}
